<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
  <app-page-hero eyebrow="CY Lite" [title]="title" [subtitle]="subtitle">
    <div actions>
      <p-button label="Inventory" icon="pi pi-table" severity="secondary" [outlined]="true" (click)="openInventory()"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" (click)="load()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <app-page-data-panel *ngIf="mode === 'occupancy'" title="Facility / Area Occupancy" subtitle="Slot utilization and yard workload with drill-down to inventory." [resultText]="(occupancy.rows?.length || 0) + ' areas'">
    <div class="ops-grid">
      <button type="button" class="ops-tile" *ngFor="let row of occupancy.rows || []" (click)="openInventory(row)">
        <div>
          <span>{{ row.facilityName || facilityName(row.facilityId) }}</span>
          <strong>{{ row.areaName || ('Area #' + row.areaId) }}</strong>
        </div>
        <div class="tile-meter"><i [style.width.%]="row.occupancyPercent || 0"></i></div>
        <div class="tile-facts">
          <span>{{ row.occupiedSlots || 0 }}/{{ row.totalSlots || 0 }} slots</span>
          <span>{{ row.activeVisits || 0 }} visits</span>
          <span>{{ row.holds || 0 }} holds</span>
          <span>{{ row.expiredFreeTimeCount || 0 }} expired</span>
        </div>
      </button>
    </div>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="mode === 'appointments'" title="Truck Appointments" subtitle="Gate appointments by facility, container, truck, and operational status." [resultText]="appointments.length + ' appointments'">
    <div class="table-action-row mb-3">
      <p-button label="New Appointment" icon="pi pi-calendar-plus" size="small" (click)="openAppointment()"></p-button>
    </div>
    <p-table [value]="appointments" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '72rem' }">
      <ng-template pTemplate="header"><tr><th>Container</th><th>Facility</th><th>Window</th><th>Truck / Driver</th><th>Status</th><th>Linked Visit</th><th></th></tr></ng-template>
      <ng-template pTemplate="body" let-appointment>
        <tr>
          <td><strong>{{ appointment.containerNumber || '-' }}</strong><div class="muted">{{ appointment.referenceNumber || '-' }}</div></td>
          <td>{{ facilityName(appointment.facilityId) }}</td>
          <td><strong>{{ appointment.scheduledStartAt | date:'short' }}</strong><div class="muted">{{ appointment.scheduledEndAt | date:'short' }}</div></td>
          <td>
            {{ appointment.truckPlateNumber || '-' }}
            <div class="muted">{{ appointment.driverName || '-' }}</div>
            <div class="muted">{{ appointment.carrierCrmClientName || appointment.carrierName || '-' }}</div>
          </td>
          <td><p-tag [value]="appointment.status || 'SCHEDULED'" [severity]="severity(appointment.status)"></p-tag></td>
          <td>{{ appointment.visitId ? ('Visit #' + appointment.visitId) : '-' }}</td>
          <td><p-button icon="pi pi-pencil" [text]="true" (click)="openAppointment(appointment)"></p-button></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7"><div class="table-empty">No truck appointments are scheduled.</div></td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="mode === 'charges'" title="Yard Charge Rules" subtitle="Storage, demurrage, detention, lift-on, and lift-off charge rules plus active visit recalculation." [resultText]="chargeRules.length + ' rules'">
    <div class="table-action-row mb-3">
      <p-button label="New Charge Rule" icon="pi pi-plus" size="small" (click)="openChargeRule()"></p-button>
    </div>
    <p-table [value]="chargeRules" styleClass="p-datatable-sm">
      <ng-template pTemplate="header"><tr><th>Rule</th><th>Facility</th><th>Container Type</th><th>Basis</th><th>Free Days</th><th>Rate</th><th>Status</th><th></th></tr></ng-template>
      <ng-template pTemplate="body" let-rule>
        <tr>
          <td>
            <strong>{{ productName(rule.productId) }}</strong>
            <div class="muted">{{ label(rule.chargeType) }} · {{ rule.areaId ? ('Area #' + rule.areaId) : 'Facility-wide' }}</div>
            <p-tag *ngIf="rule.rateOverridden" value="Rate override" severity="warn"></p-tag>
          </td>
          <td>{{ facilityName(rule.facilityId) }}</td>
          <td>{{ rule.containerType || 'All' }}</td>
          <td>{{ label(rule.chargeBasis) }}</td>
          <td>{{ rule.freeDays || 0 }}</td>
          <td>{{ rule.currencyCode || 'SAR' }} {{ rule.rateAmount || 0 | number:'1.2-2' }}</td>
          <td><p-tag [value]="rule.active === false ? 'Inactive' : 'Active'" [severity]="rule.active === false ? 'secondary' : 'success'"></p-tag></td>
          <td><p-button icon="pi pi-pencil" [text]="true" (click)="openChargeRule(rule)"></p-button></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="8"><div class="table-empty">No yard charge rules are configured.</div></td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="mode === 'charges'" title="Active Visit Charge Control" subtitle="Recalculate estimated charges for current active yard visits." [resultText]="visits.length + ' active visits'">
    <p-table [value]="visits" styleClass="p-datatable-sm">
      <ng-template pTemplate="header"><tr><th>Container</th><th>Facility</th><th>Free Time</th><th>Estimated Charge</th><th></th></tr></ng-template>
      <ng-template pTemplate="body" let-visit>
        <tr>
          <td><strong>{{ visit.containerNumber || '-' }}</strong><div class="muted">{{ visit.billOfLadingNumber || visit.bookingNumber || '-' }}</div></td>
          <td>{{ facilityName(visit.facilityId) }}</td>
          <td><p-tag [value]="visit.freeTimeExpired ? 'Expired' : 'Clear'" [severity]="visit.freeTimeExpired ? 'danger' : 'success'"></p-tag><div class="muted">{{ visit.chargeableDays || 0 }} chargeable days</div></td>
          <td><strong>{{ money(visit.estimatedChargeAmount) }}</strong></td>
          <td><p-button label="Recalculate" icon="pi pi-calculator" [text]="true" (click)="recalculate(visit)"></p-button></td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="mode === 'eirs'" title="EIR Register" subtitle="Gate-in and gate-out receipts generated from yard visits." [resultText]="allEirs().length + ' EIRs'">
    <p-table [value]="allEirs()" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '76rem' }">
      <ng-template pTemplate="header"><tr><th>EIR</th><th>Container</th><th>Facility</th><th>Event</th><th>Seal</th><th>Condition</th><th>Damage</th><th></th></tr></ng-template>
      <ng-template pTemplate="body" let-eir>
        <tr>
          <td><strong>{{ eir.eirNumber || '-' }}</strong><div class="muted">{{ eir.eirType || '-' }}</div></td>
          <td>{{ eir.containerNumber || '-' }}</td>
          <td>{{ facilityName(eir.facilityId) }}</td>
          <td>{{ eir.eventTime | date:'short' }}</td>
          <td><p-tag [value]="eir.sealVerified === false ? 'Mismatch' : 'Verified'" [severity]="eir.sealVerified === false ? 'danger' : 'success'"></p-tag><div class="muted">{{ eir.sealMismatchReason || '-' }}</div></td>
          <td><p-tag [value]="eir.conditionStatus || 'UNKNOWN'" [severity]="severity(eir.conditionStatus)"></p-tag></td>
          <td>{{ eir.damageObserved ? (eir.damageNotes || 'Damage observed') : 'No damage noted' }}</td>
          <td><p-button label="PDF" icon="pi pi-file-pdf" [text]="true" (click)="openEir(eir)"></p-button></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="8"><div class="table-empty">No EIRs were generated for active yard visits.</div></td></tr></ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>

<p-dialog
  [(visible)]="appointmentDialog"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(980px, 94vw)' }"
  [breakpoints]="{ '860px': '96vw' }"
  header="Truck Appointment"
  styleClass="master-create-dialog appointment-create-dialog">
  <section class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon">
        <i class="pi pi-calendar-plus"></i>
      </div>
      <div>
        <small>Gate Appointment</small>
        <h3>{{ appointmentTypeName() }}</h3>
        <p>Reserve a terminal gate window and capture the truck, driver, carrier, and linked container reference for yard control.</p>
      </div>
      <div class="master-dialog-facts">
        <div>
          <span>Facility</span>
          <strong>{{ appointmentFacilityName() }}</strong>
        </div>
        <div>
          <span>Window</span>
          <strong>{{ appointmentWindowLabel() }}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{{ label(appointmentForm.get('status')?.value) || 'Scheduled' }}</strong>
        </div>
      </div>
    </aside>

    <form [formGroup]="appointmentForm" class="master-dialog-form">
      <p-inputGroup class="master-wide-field">
        <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentFacility" formControlName="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
          <label for="appointmentFacility">Facility *</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-sign-in"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentType" formControlName="appointmentType" [options]="appointmentTypes" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
          <label for="appointmentType">Appointment Type *</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentStatus" formControlName="status" [options]="appointmentStatuses" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
          <label for="appointmentStatus">Status *</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-datePicker inputId="appointmentStart" formControlName="scheduledStartAt" [showTime]="true" hourFormat="12" appendTo="body" styleClass="w-full"></p-datePicker>
          <label for="appointmentStart">Start *</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-datePicker inputId="appointmentEnd" formControlName="scheduledEndAt" [showTime]="true" hourFormat="12" appendTo="body" styleClass="w-full"></p-datePicker>
          <label for="appointmentEnd">End</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentContainer" formControlName="containerNumber" [options]="containerOptions" optionLabel="label" optionValue="value" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full">
            <ng-template pTemplate="item" let-option>
              <div class="appointment-option"><strong>{{ option.label }}</strong><small *ngIf="option.hint">{{ option.hint }}</small></div>
            </ng-template>
          </p-select>
          <label for="appointmentContainer">Container</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <input id="appointmentRef" pInputText formControlName="referenceNumber">
          <label for="appointmentRef">Reference</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-truck"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentTruck" formControlName="vehicleId" [options]="truckPlateOptions" optionLabel="label" optionValue="value" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onAppointmentVehicleChange($event.value)">
            <ng-template pTemplate="item" let-option>
              <div class="appointment-option"><strong>{{ option.label }}</strong><small *ngIf="option.hint">{{ option.hint }}</small></div>
            </ng-template>
          </p-select>
          <label for="appointmentTruck">Truck Plate</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentDriver" formControlName="driverId" [options]="driverOptions" optionLabel="label" optionValue="value" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onAppointmentDriverChange($event.value)">
            <ng-template pTemplate="item" let-option>
              <div class="appointment-option"><strong>{{ option.label }}</strong><small *ngIf="option.hint">{{ option.hint }}</small></div>
            </ng-template>
          </p-select>
          <label for="appointmentDriver">Driver</label>
        </p-iftaLabel>
      </p-inputGroup>

      <p-inputGroup class="master-wide-field">
        <p-inputGroupAddon><i class="pi pi-send"></i></p-inputGroupAddon>
        <p-iftaLabel>
          <p-select inputId="appointmentCarrier" formControlName="carrierCrmClientId" [options]="carrierOptions" optionLabel="label" optionValue="value" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onAppointmentCarrierChange($event.value)">
            <ng-template pTemplate="item" let-option>
              <div class="appointment-option"><strong>{{ option.label }}</strong><small *ngIf="option.hint">{{ option.hint }}</small></div>
            </ng-template>
          </p-select>
          <label for="appointmentCarrier">CRM Transporter</label>
        </p-iftaLabel>
      </p-inputGroup>
    </form>
  </section>
  <ng-template pTemplate="footer">
    <div class="master-dialog-footer">
      <p-button type="button" label="Reset" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="openAppointment(editingAppointment || undefined)"></p-button>
      <span class="footer-spacer"></span>
      <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="appointmentDialog = false"></p-button>
      <p-button type="button" label="Save Appointment" icon="pi pi-save" (click)="saveAppointment()" [loading]="isSubmitting"></p-button>
    </div>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="chargeRuleDialog" [modal]="true" [style]="{ width: '52rem', maxWidth: '95vw' }" header="Yard Charge Rule" styleClass="p-fluid">
  <form [formGroup]="chargeRuleForm" class="ops-form-grid">
    <p-iftaLabel><p-select inputId="chargeFacility" formControlName="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select><label for="chargeFacility">Facility *</label></p-iftaLabel>
    <p-iftaLabel><p-inputNumber inputId="chargeArea" formControlName="areaId" [useGrouping]="false" styleClass="w-full"></p-inputNumber><label for="chargeArea">Area ID</label></p-iftaLabel>
    <p-iftaLabel><p-select inputId="chargeProduct" formControlName="productId" [options]="logisticsProducts" optionLabel="name" optionValue="productID" [filter]="true" filterBy="name,productCode" [showClear]="true" appendTo="body" styleClass="w-full"></p-select><label for="chargeProduct">Catalog Service</label></p-iftaLabel>
    <p-iftaLabel><p-select inputId="chargeProductVariant" formControlName="variantId" [options]="chargeProductVariants()" optionLabel="name" optionValue="variantID" [showClear]="true" appendTo="body" styleClass="w-full"></p-select><label for="chargeProductVariant">Catalog Variant</label></p-iftaLabel>
    <p-iftaLabel><p-select inputId="chargeType" formControlName="chargeType" [options]="chargeTypes" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select><label for="chargeType">Charge Type *</label></p-iftaLabel>
    <p-iftaLabel><p-select inputId="chargeBasis" formControlName="chargeBasis" [options]="chargeBasisOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select><label for="chargeBasis">Basis *</label></p-iftaLabel>
    <p-iftaLabel><input id="chargeContainerType" pInputText formControlName="containerType"><label for="chargeContainerType">Container Type</label></p-iftaLabel>
    <p-iftaLabel><p-inputNumber inputId="chargeFreeDays" formControlName="freeDays" [min]="0" [useGrouping]="false" styleClass="w-full"></p-inputNumber><label for="chargeFreeDays">Free Days</label></p-iftaLabel>
    <p-iftaLabel><input id="chargeCurrency" pInputText formControlName="currencyCode"><label for="chargeCurrency">Currency *</label></p-iftaLabel>
    <p-iftaLabel><p-inputNumber inputId="chargeRate" formControlName="rateAmount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber><label for="chargeRate">Rate *</label></p-iftaLabel>
    <div class="surface-50 border-1 surface-border border-round p-3 text-sm" *ngIf="selectedChargeProduct() as product">
      <strong>{{ product.productCode }}</strong> · Tax {{ product.taxRate || 0 }}% ·
      Income {{ product.incomeAccountName || 'company default' }} ·
      Expense {{ product.expenseAccountName || 'company default' }}
    </div>
  </form>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (click)="chargeRuleDialog = false"></p-button>
    <p-button label="Save" icon="pi pi-check" (click)="saveChargeRule()" [loading]="isSubmitting"></p-button>
  </ng-template>
</p-dialog>
